export type CalculatorInput = {
  incomingContacts: number
  periodSeconds: number
  averageHandleTimeSeconds: number
  targetServiceLevelPercent: number
  targetAnswerTimeSeconds: number
  shrinkagePercent: number
  maxOccupancyPercent: number
  averagePatienceSeconds: number
}

export type MetricSnapshot = {
  agentsAvailable: number
  scheduledAgents: number
  serviceLevel: number
  occupancy: number
  asa: number
  immediateAnswerRate: number
  abandonRate: number
  waitProbability: number
  isRecommended: boolean
  dividerBefore: boolean
}

export type CalculationResult = {
  recommendedAgentsAvailable: number
  recommendedScheduledAgents: number
  shrinkagePercent: number
  serviceLevelTarget: number
  occupancyTarget: number
  metrics: MetricSnapshot
  metServiceLevelTarget: boolean
  exceededOccupancyCap: boolean
  trafficIntensity: number
  arrivalRate: number
  searchIterations: number
  calculationWarnings: string[]
  recommendedScenarioIndex: number
  validationErrors: string[]
  whatIfScenarios: MetricSnapshot[]
}

export const formatPercent = (value: number, fractionDigits = 1) =>
  `${(value * 100).toFixed(fractionDigits)}%`

export const formatSecondsToTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return "∞"

  const safeSeconds = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(safeSeconds / 60)
  const remaining = safeSeconds % 60
  return `${minutes}:${remaining.toString().padStart(2, "0")}`
}

export const formatNumber = (value: number, fractionDigits = 0) => {
  if (!Number.isFinite(value)) return "0"

  return value.toLocaleString(undefined, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  })
}

export type PeriodOption = {
  value: string
  seconds: number
  labelKey: string
  descriptorKey: string
  disabled?: boolean
  helperKey?: string
}

export const periodOptions: ReadonlyArray<PeriodOption> = [
  {
    value: "15m",
    seconds: 15 * 60,
    labelKey: "form.periodOptions.15m.label",
    descriptorKey: "form.periodOptions.15m.descriptor",
  },
  {
    value: "30m",
    seconds: 30 * 60,
    labelKey: "form.periodOptions.30m.label",
    descriptorKey: "form.periodOptions.30m.descriptor",
  },
  {
    value: "1h",
    seconds: 60 * 60,
    labelKey: "form.periodOptions.1h.label",
    descriptorKey: "form.periodOptions.1h.descriptor",
  },
  {
    value: "8h",
    seconds: 8 * 60 * 60,
    labelKey: "form.periodOptions.8h.label",
    descriptorKey: "form.periodOptions.8h.descriptor",
  },
  {
    value: "9h",
    seconds: 9 * 60 * 60,
    labelKey: "form.periodOptions.9h.label",
    descriptorKey: "form.periodOptions.9h.descriptor",
  },
  {
    value: "10h",
    seconds: 10 * 60 * 60,
    labelKey: "form.periodOptions.10h.label",
    descriptorKey: "form.periodOptions.10h.descriptor",
  },
  {
    value: "12h",
    seconds: 12 * 60 * 60,
    labelKey: "form.periodOptions.12h.label",
    descriptorKey: "form.periodOptions.12h.descriptor",
  },
  {
    value: "24h",
    seconds: 24 * 60 * 60,
    labelKey: "form.periodOptions.24h.label",
    descriptorKey: "form.periodOptions.24h.descriptor",
  },
]

export type ReportingIntervalOption = {
  value: string
  labelKey: string
}

export const reportingIntervalOptions: ReadonlyArray<ReportingIntervalOption> = [
  { value: "15min", labelKey: "form.reportingIntervalOptions.15min" },
  { value: "30min", labelKey: "form.reportingIntervalOptions.30min" },
  { value: "60min", labelKey: "form.reportingIntervalOptions.60min" },
]

export const resolvePeriodSeconds = (periodValue: string) => {
  const match = periodOptions.find((option) => option.value === periodValue)
  return match?.seconds ?? periodOptions[0]!.seconds
}
